/**
 * InsufficientCreditsModal Component
 * Displays when user attempts an action without sufficient credits
 */

import { useEffect, useRef, useState } from 'react';
import { CreditPurchaseModal } from '../auth/CreditPurchaseModal';

export interface InsufficientCreditsModalProps {
  /** Whether modal is open */
  isOpen: boolean;
  /** Required credits for the action */
  required: number;
  /** Available credits */
  available: number;
  /** Callback when modal is closed */
  onClose: () => void;
}

/**
 * InsufficientCreditsModal component
 * Shows a modal when user doesn't have enough credits
 */
export function InsufficientCreditsModal({
  isOpen,
  required,
  available,
  onClose,
}: InsufficientCreditsModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  /**
   * Handle escape key to close modal
   */
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  /**
   * Focus trap for accessibility
   */
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      const handleTab = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      };

      document.addEventListener('keydown', handleTab);
      firstElement?.focus();

      return () => document.removeEventListener('keydown', handleTab);
    }
    return undefined;
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        animation: 'fadeIn 0.2s ease-out',
      }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="insufficient-credits-title"
    >
      <div
        ref={modalRef}
        style={{
          background: 'var(--color-surface)',
          border: '4px solid var(--color-accent)',
          borderRadius: '8px',
          padding: '2rem',
          maxWidth: '500px',
          width: '90%',
          fontFamily: 'var(--font-family)',
          animation: 'slideIn 0.3s ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Title */}
        <h2
          id="insufficient-credits-title"
          style={{
            fontSize: '1.25rem',
            color: 'var(--color-accent)',
            marginBottom: '1rem',
            textAlign: 'center',
            fontWeight: 'bold',
          }}
        >
          ❌ Insufficient Credits
        </h2>

        {/* Message */}
        <div
          style={{
            fontSize: '0.85rem',
            color: 'var(--color-fg)',
            marginBottom: '1.5rem',
            lineHeight: '1.6',
            textAlign: 'center',
          }}
        >
          <p style={{ marginBottom: '1rem' }}>
            You need <strong style={{ color: 'var(--color-accent)' }}>{required}</strong> credit
            {required !== 1 ? 's' : ''} for this action, but you only have{' '}
            <strong style={{ color: 'var(--color-accent)' }}>{available}</strong> credit
            {available !== 1 ? 's' : ''} available.
          </p>
          <p style={{ fontSize: '0.75rem', opacity: 0.8 }}>
            💡 Purchase more credits to continue using AI generation features.
          </p>
        </div>

        {/* Actions */}
        <div
          style={{
            display: 'flex',
            gap: '0.75rem',
            justifyContent: 'center',
          }}
        >
          <button
            onClick={onClose}
            style={{
              background: 'var(--color-surface)',
              border: '3px solid var(--color-cell-border)',
              color: 'var(--color-fg)',
              padding: '0.75rem 1.5rem',
              fontSize: '0.75rem',
              borderRadius: '4px',
              fontFamily: 'var(--font-family)',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--color-accent)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--color-cell-border)';
            }}
          >
            Close
          </button>
          <button
            onClick={() => {
              setShowPurchaseModal(true);
            }}
            style={{
              background: 'var(--color-accent)',
              border: '3px solid var(--color-accent)',
              color: 'var(--color-bg)',
              padding: '0.75rem 1.5rem',
              fontSize: '0.75rem',
              borderRadius: '4px',
              fontFamily: 'var(--font-family)',
              cursor: 'pointer',
              fontWeight: 'bold',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            💳 Purchase Credits
          </button>
        </div>
      </div>

      {/* Credit Purchase Modal */}
      <CreditPurchaseModal
        isOpen={showPurchaseModal}
        onClose={() => {
          setShowPurchaseModal(false);
          onClose();
        }}
      />

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideIn {
          from {
            transform: translateY(-20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}
